<script setup lang="ts">
/**
 * AssistantChip — the app's one way to show and change which assistant you
 * are talking to.
 *
 * A tinted pill carrying the assistant's icon and name, opening a menu of
 * the ones the caller can see. Shared by the Hub's ask bar and the chat
 * header so the two surfaces cannot drift into two different dropdown
 * languages — the chat header used to be a raw `<select>`, which rendered
 * OS chrome (and an OS-blue highlighted option) next to the Hub's styled
 * chip.
 *
 * With a single assistant there is nothing to choose, so the chip renders
 * as static text rather than a control that does nothing when clicked.
 */
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import type { Assistant } from "../api/conversations";
import { assistantTint, iconOf } from "../assistant-visuals";
import { resolveAssistantIcon } from "../assistant-icons";

const props = withDefaults(
  defineProps<{
    assistants: Assistant[];
    /** Slug of the selected assistant. */
    modelValue: string;
    /** Cap on the chip's width — the Hub's bar is tighter than the chat
     * header, which has the whole row to itself. */
    maxWidth?: string;
  }>(),
  { maxWidth: "13rem" },
);

const emit = defineEmits<{ (e: "update:modelValue", slug: string): void }>();

const selected = computed<Assistant | null>(
  () => props.assistants.find((a) => a.slug === props.modelValue) ?? null,
);
const selectedIcon = computed(() =>
  selected.value ? resolveAssistantIcon(iconOf(selected.value)) : null,
);
const tint = computed(() => assistantTint(props.modelValue));

const open = ref(false);
const root = ref<HTMLElement | null>(null);

function choose(slug: string): void {
  emit("update:modelValue", slug);
  open.value = false;
}
function onDocClick(e: MouseEvent) {
  if (open.value && root.value && !root.value.contains(e.target as Node)) {
    open.value = false;
  }
}
function onKey(e: KeyboardEvent) {
  if (e.key === "Escape") open.value = false;
}
onMounted(() => {
  document.addEventListener("click", onDocClick);
  document.addEventListener("keydown", onKey);
});
onBeforeUnmount(() => {
  document.removeEventListener("click", onDocClick);
  document.removeEventListener("keydown", onKey);
});
</script>

<template>
  <span ref="root" class="achip-wrap">
    <button
      v-if="assistants.length > 1"
      type="button"
      class="achip"
      :aria-expanded="open"
      aria-haspopup="listbox"
      :style="{ '--tint': tint, '--achip-max': maxWidth }"
      @click.stop="open = !open"
    >
      <component
        :is="selectedIcon"
        v-if="selectedIcon"
        :size="16"
        :stroke-width="2"
      />
      <span class="achip__name">{{ selected?.name }}</span>
      <span class="achip__caret" aria-hidden="true">▾</span>
    </button>
    <span
      v-else
      class="achip achip--static"
      :style="{ '--tint': tint, '--achip-max': maxWidth }"
    >
      <component
        :is="selectedIcon"
        v-if="selectedIcon"
        :size="16"
        :stroke-width="2"
      />
      <span class="achip__name">{{ selected?.name }}</span>
    </span>

    <Transition name="achip-fade">
      <ul v-if="open" class="achip__menu glass-strong" role="listbox">
        <li v-for="a in assistants" :key="a.slug">
          <button
            type="button"
            class="achip__item"
            role="option"
            :aria-selected="a.slug === modelValue"
            :style="{ '--tint': assistantTint(a.slug) }"
            @click="choose(a.slug)"
          >
            <component
              :is="resolveAssistantIcon(iconOf(a))"
              v-if="resolveAssistantIcon(iconOf(a))"
              :size="16"
              :stroke-width="2"
            />
            <span>{{ a.name }}</span>
          </button>
        </li>
      </ul>
    </Transition>
  </span>
</template>

<style scoped>
.achip-wrap {
  position: relative;
  display: inline-flex;
}
.achip {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  flex: none;
  max-width: var(--achip-max, 13rem);
  padding: 0.42rem 0.7rem;
  border-radius: var(--radius-pill);
  border: 1px solid color-mix(in srgb, var(--tint) 24%, transparent);
  background: color-mix(in srgb, var(--tint) 10%, transparent);
  color: var(--tint);
  font-size: 0.84rem;
  font-weight: 600;
  line-height: 1;
  cursor: pointer;
  transition: background var(--dur-1) var(--ease-out);
}
.achip--static {
  cursor: default;
}
.achip:not(.achip--static):hover {
  background: color-mix(in srgb, var(--tint) 18%, transparent);
}
.achip__name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.achip__caret {
  font-size: 0.65rem;
  opacity: 0.75;
}

.achip__menu {
  position: absolute;
  top: calc(100% + 0.45rem);
  left: 0;
  z-index: 60;
  min-width: 15rem;
  max-width: min(22rem, 90vw);
  max-height: 60vh;
  overflow-y: auto;
  padding: 0.35rem;
  border-radius: var(--radius-md);
  list-style: none;
  margin: 0;
}
.achip__item {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  width: 100%;
  padding: 0.5rem 0.6rem;
  border-radius: var(--radius-sm);
  color: var(--color-ink);
  font-size: 0.88rem;
  text-align: left;
  cursor: pointer;
  transition: background var(--dur-1) var(--ease-out);
}
.achip__item svg {
  flex: none;
  color: var(--tint);
}
.achip__item:hover,
.achip__item[aria-selected="true"] {
  background: color-mix(in srgb, var(--color-accent) 8%, transparent);
}

.achip-fade-enter-active,
.achip-fade-leave-active {
  transition:
    opacity var(--dur-1) var(--ease-out),
    transform var(--dur-1) var(--ease-out);
}
.achip-fade-enter-from,
.achip-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
